import { useMutation, useQuery } from "@apollo/client"
import moment from "moment"
import { FC, Fragment, ReactNode, useContext, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { AppContext } from "../../context"
import {
  ROLES,
  TEMP_BILLING_IMPORTS_ROUTE,
  TEMP_BILLING_RECONCILIATION_ROUTE,
} from "../../utils/constant"
import { moneyFormatter } from "../../utils/helper"
import Loader from "../layout/Loader"
import ActivityReportInput from "./ActivityReportInput"
import "./detail-page.scss"
import FlagInvoiceModal from "./FlagInvoiceModal"
import {
  CLEAR_TEMP_BILLING_INVOICES,
  ITempBillingPayment,
  ITempBillingReconcileResult,
  ITempBillingReconciliationInvoice,
  ITempBillingReconciliationLine,
  reasonLabel,
  SET_TEMP_BILLING_WEEKLY_LINE_PLACEMENT,
  TEMP_BILLING_CALC_STATUS_LABEL,
  TEMP_BILLING_OWED_STATUS_LABEL,
  TEMP_BILLING_PAYMENT_RECONCILIATION,
  TEMP_BILLING_SKIP_REASON_TEXT,
  TempBillingOwedStatus,
  UNCLEAR_TEMP_BILLING_INVOICES,
} from "./gql"
import PaidInvoicesModal from "./PaidInvoicesModal"
import StampPaymentModal from "./StampPaymentModal"

// kan-101. One payment (= one Recruiter Profit Summary import) against the
// invoices it covers, grouped by invoice number on BOTH sides, then presented
// one card per client.
//
// The match indicator is a JUDGEMENT AID, not an auto-clear: a mismatched
// invoice is still fully clearable and the executive decides. What the design
// takes away is the SHORTCUT — only an open, matched invoice gets a per-row
// Clear button, so clearing a mismatch has to go through the bulk action and is
// therefore deliberate. Orphans (an invoice number with no weekly-activity rows
// anywhere) the server refuses outright; `invoice.clearable` is the authority.
//
// kan-112 adds the two other ways an invoice is SETTLED: Accepted (an explained
// reconciling item, with a reason code) and Suspense (an unexplained difference
// parked with a note). Both satisfy the fully-reconciled gate, so the counts,
// meters and step copy on this page speak of "settled", while "cleared" keeps
// its narrow meaning. Flagging is per invoice through FlagInvoiceModal — each
// one needs its own reason or note — and reopening any settled invoice goes
// through the same bulk Reopen as unclearing always did, so every transition is
// explicit in the audit journal.
//
// The layout follows the design handoff. Every colour resolves through a
// `--tbr-*` custom property in detail-page.scss, which shares its palette with
// the list page via _tokens.scss, so both pages follow the dark-mode toggle from
// one place.
//
// "Owed to RealREPP" recomputes, per weekly line, the recruiter profit our
// contract rates say People 2.0 owes us — invoice amount − gross wage − People
// 2.0 fee (on the invoice) − employee burden (on the wage, CA or non-CA by where
// the temp lives) — and sets it beside what they paid. Like the match pill it
// is a judgement aid only and never gates clearing. Every figure can be opened
// into its worked calculation on the line, because an accountant has to be able
// to defend the number to People 2.0, not just read it.

interface IReconciliationDetailProps {
  paymentMetaId: string;
}

/** One client's invoices, plus the aggregates its header reports. */
interface IInvoiceGroup {
  client: string;
  /** Every invoice for the client — what the group header counts. */
  rows: Array<ITempBillingReconciliationInvoice>;
  /** The subset the current filters leave on screen. */
  visible: Array<ITempBillingReconciliationInvoice>;
  amountTotal: number;
  /** Σ weeklyActivityTotal and Σ profitSummaryTotal over the client's WHOLE set. */
  weeklyTotal: number;
  profitTotal: number;
  /** Cleared, accepted or in suspense. */
  settledCount: number;
  /**
   * Σ owedTotal over the client's FULLY calculated invoices only, and how many
   * that is — a partial or state-unknown total would understate the sum.
   */
  owedTotal: number;
  owedCount: number;
}

/**
 * Per-line and per-invoice tolerance on owed vs paid. People 2.0 rounds each
 * line to the cent, so a 1¢ difference is their rounding, not a dispute.
 */
const CALC_TOLERANCE = 0.05

const isNum = (value?: number | null): value is number =>
  typeof value === 'number' && isFinite(value)

const roundCents = (value: number): number => Math.round(value * 100) / 100

/** A money figure that may be absent: "--" rather than a misleading $0.00. */
const moneyText = (value?: number | null): string =>
  isNum(value) ? moneyFormatter(value) : '--'

/**
 * A per-hour money figure at four decimals ("$9.1860"): these are carried unrounded
 * so that hours × rate reproduces the owed figure, and the accountant must see the
 * digits that make it do so.
 */
const rateText = (value?: number | null): string =>
  isNum(value) ? `${value < 0 ? '-' : ''}$${Math.abs(value).toFixed(4)}` : '--'

/** Rates as entered: 5 -> "5%", 15.31 -> "15.31%". */
const pctText = (value?: number | null): string =>
  isNum(value) ? `${Number(value.toFixed(4))}%` : '--'

/** Rate periods are stored 'YYYY-MM-DD'; shown the way the reports write dates. */
const isoDateText = (value?: string | null): string =>
  value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '--'

/**
 * What "Calc mismatches only" keeps: a fully calculated invoice whose owed total
 * is off what People 2.0 paid, or one we cannot calculate until a placement ID
 * is set — both need the executive's eye.
 */
const isCalcMismatch = (invoice: ITempBillingReconciliationInvoice): boolean =>
  (invoice?.calcStatus === 'CALCULATED' && !invoice?.calcMatched)
  || invoice?.calcStatus === 'STATE_UNKNOWN'

/** The company tally's owed figure: fully calculated invoices only. */
const calculated = (
  rows: Array<ITempBillingReconciliationInvoice>,
): Pick<IInvoiceGroup, 'owedTotal' | 'owedCount'> => {
  const done = rows.filter((row) => row?.calcStatus === 'CALCULATED' && isNum(row?.owedTotal))
  return {
    owedTotal: roundCents(done.reduce((sum, row) => sum + (row?.owedTotal || 0), 0)),
    owedCount: done.length,
  }
}

/** Which state rule applied to a weekly line, in words an accountant can check. */
const stateText = (line: ITempBillingReconciliationLine): string => {
  const override = line?.placementOverrideNumber
  const source = isNum(override)
    ? `placement #${override} (set by us)`
    : isNum(line?.effectivePlacementNumber)
      ? `placement #${line?.effectivePlacementNumber}`
      : ''
  if (line?.isCalifornia === null || line?.isCalifornia === undefined) {
    if (!source) return 'unknown — the line has no placement ID'
    return `unknown — ${source} was not found`
  }
  const kind = line?.isCalifornia ? 'CA' : 'non-CA'
  return `${kind}${line?.stateCode ? ` (${line?.stateCode})` : ''} — where the temp lives on ${
    source || 'the placement'
  }, or the job's state when that is blank`
}

/** 'units_x_pay' arrives lower-case from the calc module, or as an enum key. */
const grossWageSourceText = (source?: string | null): string =>
  (source || '').toLowerCase() === 'units_x_pay' ? 'units × pay rate' : 'from file'

const owedStatusText = (status?: TempBillingOwedStatus | null): string =>
  status ? TEMP_BILLING_OWED_STATUS_LABEL[status] || status : '--'

/**
 * An EXCLUDED line owes nothing, but WHY matters to the accountant, so both the
 * Calc cell and the panel say which rule applied rather than a bare "Excluded".
 * Keyed on the calc module's reason strings.
 */
const excludedShort = (line: ITempBillingReconciliationLine): string => {
  const reason = line?.owed?.reason || ''
  if (reason.startsWith('Rolled')) return 'Rolled into RT'
  if (reason.startsWith('Expense')) return 'Expense'
  if (reason.startsWith('No amounts')) return 'No amounts'
  return 'Excluded'
}

const excludedText = (line: ITempBillingReconciliationLine): string => {
  const reason = line?.owed?.reason || ''
  const payType = line?.payType || 'This'
  if (reason.startsWith('Rolled')) {
    return `${payType} hours are billed on the RT line of the same invoice — this line's invoice `
      + `amount is ${moneyFormatter(0)}, so nothing is owed on it separately. The RT line's figure includes it.`
  }
  if (reason.startsWith('No amounts')) {
    return 'No invoice amount, wage or hours on this line — nothing is owed on it.'
  }
  return `Expense reimbursement — no recruiter profit. Billed at cost, so this line counts as ${moneyFormatter(0)} owed.`
}

const asText = (value?: string | null): string =>
  value === null || value === undefined || value === '' ? '--' : value

/** Imported units arrive as raw strings; show the numeric ones to 2 decimals. */
const unitsText = (value?: string | null): string => {
  if (value === null || value === undefined || value === '') return '--'
  const parsed = Number(value)
  return isFinite(parsed) ? parsed.toFixed(2) : value
}

const dateText = (value?: string | null): string =>
  value ? moment(value).format('MM/DD/YYYY') : '--'

/** Turns the mutation's skipped[] into one readable toast. */
const skipMessage = (result?: ITempBillingReconcileResult | null): string =>
  (result?.skipped || [])
    .map((skip) =>
      `${skip?.invoiceNumber}: ${TEMP_BILLING_SKIP_REASON_TEXT[skip?.reason] || skip?.reason}`
    )
    .join(' · ')

const BackIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 6l-6 6 6 6M5 12h14" />
  </svg>
)

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="9" y="9" width="11" height="11" rx="1.5" />
    <path d="M15 6.5V5a1 1 0 00-1-1H5a1 1 0 00-1 1v9a1 1 0 001 1h1.5" />
  </svg>
)

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
)

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
    <path d="M9 5l7 7-7 7" />
  </svg>
)

const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" />
  </svg>
)

const ReconciliationDetail: FC<IReconciliationDetailProps> = ({ paymentMetaId }) => {
  const { userRoles } = useContext(AppContext)
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE)

  // The executive is normally working the not-yet-settled pile, so that filter
  // starts ON; "mismatches only" is the narrowing tool and starts off.
  // "Suspense only" is the come-back-later view: every parked item, which are by
  // definition settled, so while it is on the open-only filter is ignored.
  const [onlyOpen, setOnlyOpen] = useState<boolean>(true)
  const [onlyMismatch, setOnlyMismatch] = useState<boolean>(false)
  const [onlySuspense, setOnlySuspense] = useState<boolean>(false)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [stampOpen, setStampOpen] = useState<boolean>(false)
  const [flagTarget, setFlagTarget] = useState<ITempBillingReconciliationInvoice | null>(null)
  // Owed to RealREPP. "Calc mismatches only" composes with the filters above by
  // AND; the other two maps are keyed on the weekly line id.
  const [onlyCalcMismatch, setOnlyCalcMismatch] = useState<boolean>(false)
  const [calcOpen, setCalcOpen] = useState<Record<string, boolean>>({})
  const [placementDraft, setPlacementDraft] = useState<Record<string, string>>({})
  const [placementEditing, setPlacementEditing] = useState<Record<string, boolean>>({})
  // Which line the in-flight set-placement mutation is for — onCompleted cannot
  // see the variables, same reason as actedKeys below.
  const placementLineId = useRef<string | null>(null)

  // What the in-flight mutation acted on, so the selection can be pruned rather
  // than wiped. A row's inline Clear only drops THAT row's tick (the handoff:
  // row Clear "clears that row's selection"); the two bulk actions leave this
  // null and empty the selection outright, as the handoff also specifies. A ref
  // rather than state because onCompleted cannot see the mutation's variables,
  // and it is always written immediately before the mutation fires.
  const actedKeys = useRef<Array<string> | null>(null)

  const { data, loading, error, refetch } = useQuery<{
    tempBillingPaymentReconciliation: {
      payment: ITempBillingPayment,
      invoices: Array<ITempBillingReconciliationInvoice>,
    }
  }>(TEMP_BILLING_PAYMENT_RECONCILIATION, {
    // The query is NOT paginated — ask for the whole payment and filter on the
    // client, so the group headers can keep describing the group's WHOLE invoice
    // set while the body shows the filtered subset. Server-side filtering would
    // make "0 of 0 settled" the moment the open-only filter hid a settled row.
    variables: { paymentMetaId, onlyUncleared: false },
    skip: !paymentMetaId,
    // TempBillingReconciliationInvoice has no id, so Apollo cannot normalize it —
    // always go to the network and refetch after every mutation.
    fetchPolicy: 'network-only',
  })

  const afterReconcile = (result: ITempBillingReconcileResult | undefined, verb: string) => {
    if (result?.affectedCount) {
      toast.success(`${result?.affectedCount} invoice(s) ${verb}`)
    }
    if (result?.skipped?.length) {
      toast.warning(`${result?.skipped?.length} skipped — ${skipMessage(result)}`)
    }
    if (!result?.affectedCount && !result?.skipped?.length) {
      toast.info('Nothing to do')
    }
    const acted = actedKeys.current
    actedKeys.current = null
    setSelected((prev) => {
      if (!acted) return {}
      const next = { ...prev }
      acted.forEach((key) => delete next[key])
      return next
    })
    refetch()
  }

  const [clearInvoices, { loading: clearing }] = useMutation(CLEAR_TEMP_BILLING_INVOICES, {
    onCompleted: (res) => afterReconcile(res?.clearTempBillingInvoices, 'cleared'),
    onError: (err) => toast.error(err?.message || "something went wrong"),
  })

  const [unclearInvoices, { loading: unclearing }] = useMutation(UNCLEAR_TEMP_BILLING_INVOICES, {
    onCompleted: (res) => afterReconcile(res?.unclearTempBillingInvoices, 'reopened'),
    onError: (err) => toast.error(err?.message || "something went wrong"),
  })

  // Our-side placement id for a line People 2.0 sent without one. The imported
  // row is never touched; the server stores the id (with who and when) beside it,
  // and the state — hence the burden rate — follows it on the refetch.
  const [setLinePlacement, { loading: savingPlacement }] = useMutation(
    SET_TEMP_BILLING_WEEKLY_LINE_PLACEMENT,
    {
      onCompleted: (res) => {
        const result = res?.setTempBillingWeeklyLinePlacement
        const lineId = placementLineId.current
        placementLineId.current = null
        if (isNum(result?.placementNumber)) {
          toast.success(`Placement #${result?.placementNumber} set on the line`)
        } else {
          toast.success("Placement ID cleared — the line is back to the file's value")
        }
        if (lineId) {
          setPlacementEditing((prev) => ({ ...prev, [lineId]: false }))
          setPlacementDraft((prev) => {
            const next = { ...prev }
            delete next[lineId]
            return next
          })
        }
        refetch()
      },
      onError: (err) => {
        placementLineId.current = null
        toast.error(err?.message || "something went wrong")
      },
    },
  )

  /**
   * One card per client, each sorted alphabetically by temp employee. Grouping
   * here rather than server-side keeps the mutations key-based and untouched —
   * "Clear Selected" still spans every client, since selection is keyed on
   * invoiceKey and is unaffected by which card a row is drawn in.
   *
   * `rows` is the group's WHOLE invoice set and feeds the header aggregates;
   * `visible` is what the filters leave and feeds the table and select-all.
   *
   * Declared ABOVE the isExecutive early return: hooks must run in the same order
   * on every render, and isExecutive can flip once `me` resolves. Keyed on the raw
   * Apollo array rather than the `|| []` fallback, which would be a new reference
   * every render and defeat the memo.
   */
  const invoiceList = data?.tempBillingPaymentReconciliation?.invoices
  const groups: Array<IInvoiceGroup> = useMemo(() => {
    const byClient: Record<string, Array<ITempBillingReconciliationInvoice>> = {}
    ;(invoiceList || []).forEach((invoice) => {
      const client = invoice?.companyName?.trim() || '—'
      if (byClient[client]) byClient[client].push(invoice)
      else byClient[client] = [invoice]
    })
    const collate = (a?: string | null, b?: string | null) =>
      (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' })

    return Object.entries(byClient)
      .sort(([a], [b]) => collate(a, b))
      .map(([client, rows]) => {
        const sorted = [...rows].sort((a, b) => collate(a?.employeeName, b?.employeeName))
        return {
          client,
          rows: sorted,
          visible: sorted.filter((invoice) => {
            if (onlySuspense) {
              if (invoice?.status !== 'SUSPENSE') return false
            } else if (onlyOpen && invoice?.status !== 'OPEN') {
              return false
            }
            // An orphan has no weekly-activity rows at all, which is the loudest
            // mismatch there is — it must never be filtered out as "matched".
            if (onlyMismatch && invoice?.matched && !invoice?.orphan) return false
            if (onlyCalcMismatch && !isCalcMismatch(invoice)) return false
            return true
          }),
          amountTotal: sorted.reduce((sum, row) => sum + (row?.invoiceAmountTotal || 0), 0),
          weeklyTotal: sorted.reduce((sum, row) => sum + (row?.weeklyActivityTotal || 0), 0),
          profitTotal: sorted.reduce((sum, row) => sum + (row?.profitSummaryTotal || 0), 0),
          settledCount: sorted.filter((row) => row?.status !== 'OPEN').length,
          ...calculated(sorted),
        }
      })
  }, [invoiceList, onlyOpen, onlyMismatch, onlySuspense, onlyCalcMismatch])

  // Client-side gate is convenience only — every operation is guarded server-side.
  if (!isExecutive) return null

  const payment = data?.tempBillingPaymentReconciliation?.payment
  const invoices = invoiceList || []
  const busy = clearing || unclearing

  // Payment-level totals come from the PAYMENT record, never from the filtered
  // view — the handoff is explicit that these describe the whole file.
  const invoiceCount = payment?.invoiceCount || 0
  const clearedCount = payment?.clearedCount || 0
  const acceptedCount = payment?.acceptedCount || 0
  const suspenseCount = payment?.suspenseCount || 0
  const settledCount = payment?.settledCount || 0
  // Unresolved orphans only — an accepted or suspense orphan is a decision.
  const orphanCount = payment?.orphanCount || 0
  const settledPercent = invoiceCount ? Math.round((settledCount / invoiceCount) * 100) : 0
  const remaining = Math.max(invoiceCount - settledCount, 0)

  const stamped = !!payment?.stamped
  const reconciled = !!payment?.fullyReconciled
  const activityMonth = payment?.activityReportMonth || ''
  const activityUnlocked = !!payment?.activityReportAssignable || !!activityMonth

  // Settled rows are selectable too — that is the ONLY way to reopen one — so
  // the two bulk buttons key off WHAT is selected rather than how many.
  //
  // Derived from the VISIBLE rows, not the whole invoice list: a tick survives
  // the row being filtered out (turn the filter back off and it is still there),
  // but a bulk action must never write to rows the executive cannot see. Both
  // buttons and the "n selected" label therefore describe exactly what is on
  // screen — select 8 open rows, tick "Mismatches only", and Clear Selected
  // clears the 2 that remain rather than all 8.
  const visibleInvoices = groups.reduce<Array<ITempBillingReconciliationInvoice>>(
    (all, group) => all.concat(group.visible),
    [],
  )
  const selectedOpenKeys = visibleInvoices
    .filter((invoice) => selected[invoice?.invoiceKey] && invoice?.status === 'OPEN')
    .map((invoice) => invoice?.invoiceKey)
  const selectedSettledKeys = visibleInvoices
    .filter((invoice) => selected[invoice?.invoiceKey] && invoice?.status !== 'OPEN')
    .map((invoice) => invoice?.invoiceKey)
  const selectedTotal = selectedOpenKeys.length + selectedSettledKeys.length

  /**
   * `rowLevel` marks the per-row Clear button: on success only that row's tick
   * drops. The bulk actions leave it false, which empties the whole selection.
   */
  const runClear = (invoiceNumbers: Array<string>, rowLevel = false) => {
    if (!invoiceNumbers?.length) return
    actedKeys.current = rowLevel ? invoiceNumbers : null
    clearInvoices({ variables: { input: { paymentMetaId, invoiceNumbers } } })
  }

  /** Reopens cleared, accepted and suspense invoices alike, back to Open. */
  const runUnclear = (invoiceNumbers: Array<string>) => {
    if (!invoiceNumbers?.length) return
    // Reopen is bulk-only, so it always empties the selection.
    actedKeys.current = null
    unclearInvoices({ variables: { input: { paymentMetaId, invoiceNumbers } } })
  }

  const copyReference = async () => {
    const reference = payment?.paymentReferenceId
    if (!reference) return
    try {
      await navigator.clipboard.writeText(reference)
      toast.success('Payment reference copied')
    } catch {
      toast.error('Copy failed — please copy the reference manually')
    }
  }

  const toggleRow = (key: string) =>
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }))

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

  const toggleCalc = (lineId: string) =>
    setCalcOpen((prev) => ({ ...prev, [lineId]: !prev[lineId] }))

  /** What the placement input shows: the typed draft, else the current override. */
  const placementValue = (line: ITempBillingReconciliationLine): string =>
    placementDraft[line?.id] ?? (isNum(line?.placementOverrideNumber)
      ? String(line?.placementOverrideNumber)
      : '')

  /** `clear` removes our-side id, returning the line to the file's (missing) value. */
  const savePlacement = (line: ITempBillingReconciliationLine, clear = false) => {
    let placementNumber: number | null = null
    if (!clear) {
      const raw = placementValue(line).trim()
      // Placement.placementId — the number recruiters see, never the record uuid.
      if (!/^\d+$/.test(raw)) {
        toast.error('Enter the placement ID as a whole number, e.g. 10423')
        return
      }
      placementNumber = Number(raw)
    }
    placementLineId.current = line?.id
    setLinePlacement({
      variables: { input: { weeklyLineId: line?.id, placementNumber } },
    })
  }

  /** Select-all covers the group's VISIBLE rows, per the handoff. */
  const toggleGroup = (group: IInvoiceGroup, checked: boolean) =>
    setSelected((prev) => {
      const next = { ...prev }
      group.visible.forEach((invoice) => {
        if (checked) next[invoice?.invoiceKey] = true
        else delete next[invoice?.invoiceKey]
      })
      return next
    })

  const matchPill = (invoice: ITempBillingReconciliationInvoice) => {
    if (invoice?.orphan) return <span className="tbr-pill is-danger">No weekly activity</span>
    return invoice?.matched
      ? <span className="tbr-pill is-active">Match</span>
      : <span className="tbr-pill is-warn">Off</span>
  }

  /**
   * Owed to RealREPP for the invoice. A partial total is shown but marked, since
   * it leaves out lines that could not be calculated; with a state unknown there
   * is no single answer, so both candidates are shown instead.
   */
  const owedCell = (invoice: ITempBillingReconciliationInvoice) => {
    if (invoice?.calcStatus === 'CALCULATED') {
      return (
        <div className="tbr-owed-cell tbr-strong">{moneyText(invoice?.owedTotal)}</div>
      )
    }
    if (invoice?.calcStatus === 'PARTIAL') {
      return (
        <div
          className="tbr-owed-cell"
          title="Some lines could not be calculated (week ending before 1/1/2026, no rate period, or no amounts). This is the total of the lines that could."
        >
          <span>{moneyText(invoice?.owedTotal)}</span>
          <span className="tbr-note is-warn">partial</span>
        </div>
      )
    }
    if (invoice?.calcStatus === 'STATE_UNKNOWN') {
      return (
        <div
          className="tbr-owed-cell"
          title="A line has no placement, so whether the temp lives in California is unknown. These are the invoice totals under the CA and the non-CA burden rate."
        >
          <span className="tbr-note">CA {moneyText(invoice?.owedTotalIfCa)}</span>
          <span className="tbr-note">other {moneyText(invoice?.owedTotalIfNonCa)}</span>
        </div>
      )
    }
    return (
      <div
        className="tbr-owed-cell tbr-secondary"
        title="Not calculated: weeks ending before 1/1/2026, no rate period, or no amounts on the lines."
      >
        —
      </div>
    )
  }

  /** Our recalculation against the weekly activity report's recruiter profit. */
  const calcPill = (invoice: ITempBillingReconciliationInvoice) => {
    if (invoice?.calcStatus === 'CALCULATED') {
      return invoice?.calcMatched
        ? (
          <span
            className="tbr-pill is-active"
            title="What our contract rates say People 2.0 owed agrees with the recruiter profit on the weekly activity report, within $0.05."
          >
            Match
          </span>
        )
        : (
          <span
            className="tbr-pill is-danger"
            title={`Owed to RealREPP less the weekly activity recruiter profit is ${moneyText(
              invoice?.calcDifference,
            )} — positive means People 2.0 paid less than our rates give. Open the backing records and use Calculation on each line to see where. Indicator only; it does not stop clearing.`}
          >
            Off {moneyText(invoice?.calcDifference)}
          </span>
        )
    }
    if (invoice?.calcStatus === 'STATE_UNKNOWN') {
      return (
        <span
          className="tbr-pill is-warn"
          title={`${invoice?.linesMissingPlacement || 'A'} line(s) have no placement we can find, so whether the CA burden rate applies is unknown. Set the placement ID on the line in the backing records.`}
        >
          No placement
        </span>
      )
    }
    return (
      <span
        className="tbr-pill is-neutral"
        title={`${TEMP_BILLING_CALC_STATUS_LABEL[invoice?.calcStatus] || 'Not calculated'} — only a fully calculated invoice is compared.`}
      >
        n/a
      </span>
    )
  }

  /** Per-line owed: the figure, or both candidates when the state is unknown. */
  const lineOwedCell = (line: ITempBillingReconciliationLine) => {
    const owed = line?.owed
    if (owed?.status === 'STATE_UNKNOWN') {
      return (
        <div className="tbr-owed-cell">
          <span className="tbr-note">CA {moneyText(owed?.owedIfCa)}</span>
          <span className="tbr-note">other {moneyText(owed?.owedIfNonCa)}</span>
        </div>
      )
    }
    return (
      <div className="tbr-right tbr-num">{isNum(owed?.owed) ? moneyText(owed?.owed) : '—'}</div>
    )
  }

  /** owed − paid on a calculated line; null when there is nothing to compare. */
  const lineDifference = (line: ITempBillingReconciliationLine): number | null => {
    const owed = line?.owed?.owed
    return line?.owed?.status === 'CALCULATED' && isNum(owed)
      ? roundCents(owed - (line?.paidRecruiterProfitNum || 0))
      : null
  }

  const lineCalcCell = (line: ITempBillingReconciliationLine) => {
    const owed = line?.owed
    const difference = lineDifference(line)
    const off = difference !== null && Math.abs(difference) > CALC_TOLERANCE
    const open = !!calcOpen[line?.id]
    const tone = off ? ' is-danger' : owed?.status === 'STATE_UNKNOWN' ? ' is-warn' : ''
    return (
      <div className="tbr-line-calc">
        <span className={`tbr-note${tone}`} title={owed?.reason || ''}>
          {off
            ? `Off ${moneyText(difference)}`
            : owed?.status === 'EXCLUDED' ? excludedShort(line) : owedStatusText(owed?.status)}
        </span>
        <button
          type="button"
          className="tbr-link-btn"
          aria-expanded={open}
          onClick={() => toggleCalc(line?.id)}
        >
          {open ? 'Hide calculation' : 'Calculation'}
        </button>
      </div>
    )
  }

  /** One label / value line of the worked calculation. */
  const calcRow = (label: string, value: ReactNode, total = false) => (
    <Fragment key={label}>
      <div className={`tbr-calc-label${total ? ' tbr-calc-total' : ''}`}>{label}</div>
      <div className={`tbr-calc-value${total ? ' tbr-calc-total' : ''}`}>{value}</div>
    </Fragment>
  )

  /**
   * The worked calculation for one weekly line, in the order an accountant
   * would check it: which rates, which state, the two amounts from the file, the
   * two deductions, the result, then what People 2.0 actually paid. The per-hour
   * form is a cross-check only — the amounts are what the formula uses, because
   * that is what People 2.0's own figure follows.
   */
  /**
   * The invoice's lines that People 2.0 rolled into the RT line (OT/DT/holiday
   * with a $0 invoice amount). Their hours are billed on the RT line, so the RT
   * line's amounts legitimately exceed its own hours × rate.
   */
  const rolledSiblings = (
    invoice: ITempBillingReconciliationInvoice,
    line: ITempBillingReconciliationLine,
  ): Array<ITempBillingReconciliationLine> =>
    (invoice?.lines || []).filter(
      (other) => other?.id !== line?.id && (other?.owed?.reason || '').startsWith('Rolled'),
    )

  const calcPanel = (line: ITempBillingReconciliationLine, invoice: ITempBillingReconciliationInvoice) => {
    const owed = line?.owed
    const paid = line?.paidRecruiterProfitNum || 0
    const status = owed?.status

    if (status === 'EXCLUDED') {
      return (
        <div className="tbr-calc-panel">
          <div className="tbr-calc-message">{excludedText(line)}</div>
          {calcRow('People 2.0 paid', moneyFormatter(paid))}
        </div>
      )
    }

    if (status !== 'CALCULATED' && status !== 'STATE_UNKNOWN') {
      return (
        <div className="tbr-calc-panel">
          <div className="tbr-calc-message">
            Not calculated: {owed?.reason || 'the line lacks the figures the calculation needs'}.
          </div>
          {calcRow('People 2.0 paid', moneyFormatter(paid))}
        </div>
      )
    }

    const known = status === 'CALCULATED'
    const difference = lineDifference(line)
    const units = line?.unitsNum
    // The per-hour working needs the two rates, the hours and a known burden
    // rate (so not while the state is unknown).
    const perHour = known
      && isNum(line?.billRateNum) && isNum(line?.payRateNum) && isNum(units)
      && isNum(owed?.feePerHour) && isNum(owed?.burdenPerHour) && isNum(owed?.profitPerHour)
    const hoursText = isNum(units) ? `${units.toFixed(2)} hours` : ''
    const unitsDisagree = owed?.unitsAgree === false

    // Lines rolled into this one (only an RT line ever absorbs them). Their hours
    // explain an RT line whose amounts exceed its own hours × rate.
    const rolled = (line?.payType || '').trim().toUpperCase() === 'RT' ? rolledSiblings(invoice, line) : []
    const rolledHours = rolled.length && rolled.every((other) => isNum(other?.unitsNum))
      ? rolled.reduce((sum, other) => sum + (other?.unitsNum as number), 0)
      : null
    const rolledHoursText = rolled.length
      ? rolled
        .map((other) => `${isNum(other?.unitsNum) ? other.unitsNum.toFixed(2) : '--'} ${other?.payType || ''} hours`)
        .join(' and ')
      : ''
    // "matching the profit summary" is only claimed when it is true: the profit
    // summary row carries the invoice's TOTAL hours, so RT + rolled should equal it.
    const profitUnits = Number(invoice?.profitLines?.[0]?.units)
    const rolledMatchesProfit = isNum(units) && isNum(rolledHours) && isFinite(profitUnits)
      && Math.abs(units + rolledHours - profitUnits) < 0.011

    // The amounts working: invoice − gross wage − fee − burden. This is the figure
    // the formula actually uses (it is what People 2.0 pays on); shown as the main
    // working when the per-hour path is unavailable, and as the deciding line when
    // the file's hours do not agree with its amounts.
    const amountsRows = (total: boolean) => (
      <>
        {calcRow('Invoice amount', moneyText(owed?.invoiceAmount))}
        {calcRow(
          'Gross wage',
          `${moneyText(owed?.grossWage)} (${grossWageSourceText(owed?.grossWageSource)})`,
        )}
        {calcRow('People 2.0 fee', `${pctText(owed?.feePct)} × invoice = ${moneyText(owed?.fee)}`)}
        {known
          ? calcRow(
            'Employee burden',
            `${pctText(owed?.burdenPct)} × gross wage = ${moneyText(owed?.burden)}`,
          )
          : calcRow('Employee burden', 'depends on whether the temp lives in CA — see both results below')}
        {known
          ? calcRow(
            unitsDisagree ? 'Owed (from billed amounts)' : 'Owed',
            `invoice − gross wage − fee − burden = ${moneyText(owed?.owed)}`,
            total,
          )
          : (
            <>
              {calcRow('Owed if the temp lives in CA', moneyText(owed?.owedIfCa), true)}
              {calcRow('Owed if the temp lives outside CA', moneyText(owed?.owedIfNonCa))}
            </>
          )}
      </>
    )

    return (
      <div className="tbr-calc-panel">
        {calcRow('Rate period', `from ${isoDateText(owed?.rateEffectiveFrom)}`)}
        {calcRow('State', stateText(line))}

        {perHour
          ? (
            <>
              {/* The per-hour working, the way the accountant checks it by hand.
                  The per-hour figures are carried at full precision and only the
                  final product is rounded, so it lands on the owed figure to the
                  cent — rounding each step to cents first drifted it by 13¢ on a
                  32.5-hour line. */}
              {calcRow('Bill rate', `${moneyText(line?.billRateNum)} per hour`)}
              {calcRow('Pay rate', `${moneyText(line?.payRateNum)} per hour`)}
              {calcRow('People 2.0 fee', `${pctText(owed?.feePct)} × bill rate = ${rateText(owed?.feePerHour)} per hour`)}
              {calcRow('Employee burden', `${pctText(owed?.burdenPct)} × pay rate = ${rateText(owed?.burdenPerHour)} per hour`)}
              {calcRow(
                'Profit per hour',
                `${moneyText(line?.billRateNum)} − ${moneyText(line?.payRateNum)}`
                + ` − ${rateText(owed?.feePerHour)} − ${rateText(owed?.burdenPerHour)}`
                + ` = ${rateText(owed?.profitPerHour)}`,
              )}
              {unitsDisagree
                ? rolled.length
                  ? (
                    <>
                      {/* The hours gap is explained: this RT line bills the rolled-in
                          lines' hours too, so hours × RT rate cannot reproduce the
                          amount and the per-hour "by the file's hours" figure would
                          only mislead. Straight to the amounts, with the explanation. */}
                      {amountsRows(true)}
                      <div className="tbr-calc-note">
                        This RT line's amounts include the {rolledHoursText} rolled into it
                        {isNum(units) && isNum(rolledHours)
                          ? ` (${units.toFixed(2)} + ${rolledHours.toFixed(2)} = ${(units + rolledHours).toFixed(2)} hours${
                            rolledMatchesProfit ? ', matching the profit summary' : ''
                          })`
                          : ''}
                        , so hours × RT rate will not reproduce the amount. Owed is taken from the billed amounts.
                      </div>
                    </>
                  )
                  : (
                    <>
                      {calcRow(
                        "By the file's hours",
                        `${rateText(owed?.profitPerHour)} × ${hoursText} = ${moneyText(owed?.owedFromHours)}`,
                      )}
                      {amountsRows(true)}
                      <div className="tbr-calc-warn">
                        The file's hours ({hoursText}) do not agree with its amounts
                        {isNum(owed?.impliedHours) ? ` — invoice ÷ bill rate = ${owed.impliedHours.toFixed(2)} hours` : ''}
                        , so owed is taken from the billed amounts, which is what People 2.0 pays on.
                      </div>
                    </>
                  )
                : calcRow('Owed', `${rateText(owed?.profitPerHour)} × ${hoursText} = ${moneyText(owed?.owed)}`, true)}
            </>
          )
          : amountsRows(true)}

        {calcRow(
          'People 2.0 paid',
          known && difference !== null
            ? `${moneyFormatter(paid)} · difference (owed − paid) ${moneyFormatter(difference)}`
            : moneyFormatter(paid),
        )}
      </div>
    )
  }

  /**
   * Placed ID on a weekly line. A clean id from the file is just shown. A
   * missing one (People 2.0 omission) gets an inline form to set our-side id,
   * and an id we set shows who set it and when, with a pencil to change or
   * clear it. A clean id that matches no placement is editable the same way,
   * since it leaves the state just as unknown.
   */
  const placementCell = (line: ITempBillingReconciliationLine) => {
    const override = line?.placementOverrideNumber
    const hasOverride = isNum(override)
    const notFound = !line?.placementIdMissing && !hasOverride && !line?.placementFound
    if (!line?.placementIdMissing && !hasOverride && !notFound) {
      return <div className="tbr-num">{asText(line?.placementId)}</div>
    }

    // A missing id with nothing set yet: the form is simply there.
    const editing = (line?.placementIdMissing && !hasOverride) || !!placementEditing[line?.id]
    const value = placementValue(line)
    const pencil = !editing && (
      <button
        type="button"
        className="tbr-link-btn"
        title={hasOverride ? 'Change or clear the placement ID' : 'Set the placement ID'}
        aria-label={hasOverride ? 'Change the placement ID' : 'Set the placement ID'}
        onClick={() => setPlacementEditing((prev) => ({ ...prev, [line?.id]: true }))}
      >
        <PencilIcon />
      </button>
    )

    return (
      <div className="tbr-placement-cell">
        <div className="tbr-placement-set">
          {hasOverride
            ? (
              <span className="tbr-num">
                #{override} set by {line?.placementOverrideByName || '--'}
                {line?.placementOverrideAt ? ` · ${dateText(line?.placementOverrideAt)}` : ''}
              </span>
            )
            : (
              <span
                className="tbr-note is-warn"
                title={line?.placementId ? `The file has "${line?.placementId}"` : 'The file has no placement ID'}
              >
                {line?.placementIdMissing ? 'missing' : `#${line?.placementId} not found`}
              </span>
            )}
          {pencil}
        </div>

        {editing && (
          <div className="tbr-placement-form">
            <input
              type="text"
              inputMode="numeric"
              placeholder="Placement ID"
              aria-label="Placement ID"
              value={value}
              disabled={savingPlacement}
              onChange={({ target }) =>
                setPlacementDraft((prev) => ({ ...prev, [line?.id]: target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') savePlacement(line)
              }}
            />
            <button
              type="button"
              className="tbr-btn is-sm is-outline-action"
              disabled={savingPlacement || !value.trim()}
              onClick={() => savePlacement(line)}
            >
              Save
            </button>
            {hasOverride && (
              <button
                type="button"
                className="tbr-btn is-sm is-outline-neutral"
                disabled={savingPlacement}
                title="Remove our placement ID — the line goes back to the file's value"
                onClick={() => savePlacement(line, true)}
              >
                Clear
              </button>
            )}
            {(hasOverride || notFound) && (
              <button
                type="button"
                className="tbr-link-btn"
                disabled={savingPlacement}
                onClick={() => {
                  setPlacementEditing((prev) => ({ ...prev, [line?.id]: false }))
                  setPlacementDraft((prev) => {
                    const next = { ...prev }
                    delete next[line?.id]
                    return next
                  })
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  /** "name · date" — who settled the invoice and when. */
  const stampLine = (invoice: ITempBillingReconciliationInvoice) => (
    <span className="tbr-stamp-line">
      {invoice?.clearedByName || '--'}
      {invoice?.clearedAt ? ` · ${dateText(invoice?.clearedAt)}` : ''}
    </span>
  )

  const changeButton = (invoice: ITempBillingReconciliationInvoice) =>
    invoice?.flaggable && (
      <button
        type="button"
        className="tbr-link-btn"
        disabled={busy}
        title="Change the status, reason or note"
        onClick={() => setFlagTarget(invoice)}
      >
        Change
      </button>
    )

  const statusCell = (invoice: ITempBillingReconciliationInvoice) => {
    if (invoice?.status === 'CLEARED') {
      return (
        <div className="tbr-status-cell">
          <span className="tbr-pill is-active">Cleared</span>
          {stampLine(invoice)}
        </div>
      )
    }

    // A flagged invoice shows WHY on the row itself — reason code first, then the
    // note — so the executive can read every reconciling item and every parked
    // difference down the column without opening anything. The full note is on
    // hover where it is too long for the cell.
    if (invoice?.status === 'ACCEPTED') {
      const reason = reasonLabel(invoice?.flagReason)
      const line = `${reason}${invoice?.flagNote ? `${reason ? ' · ' : ''}${invoice?.flagNote}` : ''}`
      return (
        <div className="tbr-status-cell">
          <span className="tbr-pill is-neutral">Accepted</span>
          {!!line && (
            <span className="tbr-flag-line" title={invoice?.flagNote || reason}>{line}</span>
          )}
          {stampLine(invoice)}
          {changeButton(invoice)}
        </div>
      )
    }

    if (invoice?.status === 'SUSPENSE') {
      return (
        <div className="tbr-status-cell">
          <span className="tbr-pill is-warn">Suspense</span>
          {!!invoice?.flagNote && (
            <span className="tbr-flag-line" title={invoice?.flagNote}>{invoice?.flagNote}</span>
          )}
          {stampLine(invoice)}
          {changeButton(invoice)}
        </div>
      )
    }

    // Open. A Clear button only where clearing is the obvious call: matched, and
    // the server agrees it is clearable. Everything else goes through bulk Clear
    // or, for an orphan or a difference that cannot be cleared as-is, Flag.
    // No "Open" pill: the action buttons already say the invoice is not settled,
    // and the pill pushed them onto a second line.
    return (
      <div className="tbr-status-cell">
        {!!invoice?.blockedByPaymentMetaId && (
          <span className="tbr-blocked-line">
            Settled on {invoice?.blockedByPaymentReferenceId || invoice?.blockedByPaymentMetaId}
          </span>
        )}
        {((invoice?.matched && invoice?.clearable) || invoice?.flaggable) && (
          <div className="tbr-status-actions">
            {/* A matched invoice has no difference to explain, so Accepted is off
                the table and the button says the one thing it can do — Suspense
                is still legitimate there (wrong client, wrong period, duplicate)
                because the match test only compares the two profit sums. */}
            {invoice?.flaggable && (
              <button
                type="button"
                className="tbr-btn is-sm is-outline"
                disabled={busy}
                title={invoice?.acceptable
                  ? 'Accept as an explained reconciling item, or put in suspense'
                  : 'Put in suspense — a matched invoice is cleared or held, never accepted'}
                onClick={() => setFlagTarget(invoice)}
              >
                {invoice?.acceptable ? 'Flag…' : 'Suspense…'}
              </button>
            )}
            {invoice?.matched && invoice?.clearable && (
              <button
                type="button"
                className="tbr-btn is-sm is-outline-action"
                disabled={busy}
                onClick={() => runClear([invoice?.invoiceKey], true)}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  const backingRecords = (invoice: ITempBillingReconciliationInvoice) => (
    <div className="tbr-records">
      <div className="tbr-records-caption">Backing Records</div>

      {/* Both reports' rows for this invoice, in ONE table keyed by report type.
          Weekly activity first (what we billed), then the profit summary (what
          People 2.0 paid). Pay type only exists on the weekly side. */}
      <div className="tbr-records-table">
        <div className="tbr-rec-head">
          <div>Report Type</div>
          <div>Placed ID</div>
          <div>Temp Employee</div>
          <div>Invoice Date</div>
          <div>Week Ending</div>
          <div>Pay Type</div>
          <div className="tbr-right">Units</div>
          <div className="tbr-right">Invoice Amt</div>
          <div className="tbr-right">Recruiter Profit</div>
          {/* Owed to RealREPP per weekly line — the profit-summary side has no
              pay type or wage, so it is never recalculated. */}
          <div className="tbr-right">Owed</div>
          <div>Calc</div>
        </div>

        {!invoice?.lines?.length && (
          <div className="tbr-rec-notice">
            No weekly activity rows carry this invoice number in any import —
            accept it (with a reason) or put it in suspense to proceed.
          </div>
        )}

        {invoice?.lines?.map((line) => (
          <Fragment key={`w-${line?.id}`}>
            <div className="tbr-rec-row">
              <div className="tbr-rec-type">Weekly Activity Report</div>
              {placementCell(line)}
              <div className="tbr-trunc">{asText(line?.candidateName)}</div>
              <div className="tbr-num">{asText(line?.invoiceDate)}</div>
              <div className="tbr-num">{asText(line?.periodEndDate)}</div>
              <div>{asText(line?.payType)}</div>
              <div className="tbr-right tbr-num">{unitsText(line?.units)}</div>
              <div className="tbr-right tbr-num">{moneyFormatter(line?.invoiceAmountNum || 0)}</div>
              <div className="tbr-right tbr-num">
                {moneyFormatter(line?.paidRecruiterProfitNum || 0)}
              </div>
              {lineOwedCell(line)}
              {lineCalcCell(line)}
            </div>
            {!!calcOpen[line?.id] && calcPanel(line, invoice)}
          </Fragment>
        ))}

        {invoice?.profitLines?.map((line) => (
          <div className="tbr-rec-row" key={`p-${line?.id}`}>
            <div className="tbr-rec-type">Recruiter Profit Summary</div>
            <div className="tbr-num">{asText(line?.placementId)}</div>
            <div className="tbr-trunc">{asText(line?.employeeName)}</div>
            <div className="tbr-num">{asText(line?.invoiceDate)}</div>
            <div className="tbr-num">{asText(line?.periodEndDate)}</div>
            {/* Pay type is a weekly-activity concept only. */}
            <div>--</div>
            <div className="tbr-right tbr-num">{unitsText(line?.units)}</div>
            <div className="tbr-right tbr-num">{moneyFormatter(line?.invoiceAmountNum || 0)}</div>
            <div className="tbr-right tbr-num">
              {moneyFormatter(line?.grossRecruiterProfitNum || 0)}
            </div>
            <div />
            <div />
          </div>
        ))}
      </div>
    </div>
  )

  const invoiceRow = (invoice: ITempBillingReconciliationInvoice) => {
    const key = invoice?.invoiceKey
    const open = !!expanded[key]

    return (
      <Fragment key={key}>
        <div className="tbr-inv-row">
          <div className="tbr-tick">
            <input
              type="checkbox"
              title={`Select ${invoice?.invoiceNumber}`}
              checked={!!selected[key]}
              onChange={() => toggleRow(key)}
            />
          </div>

          <button
            type="button"
            className={`tbr-expander${open ? ' is-open' : ''}`}
            title="Show backing records"
            aria-expanded={open}
            onClick={() => toggleExpanded(key)}
          >
            <ChevronIcon />
          </button>

          {/* The number is the second handle on the same toggle as the chevron —
              a bigger target than a 20px glyph, and the thing the eye lands on. */}
          <button
            type="button"
            className="tbr-invoice-no tbr-trunc"
            title={`${invoice?.invoiceNumber} — show backing records`}
            aria-expanded={open}
            onClick={() => toggleExpanded(key)}
          >
            {asText(invoice?.invoiceNumber)}
          </button>
          <div className="tbr-employee-cell">
            <div className="tbr-secondary tbr-trunc" title={invoice?.employeeName || ''}>
              {asText(invoice?.employeeName)}
            </div>
            {/* A line People 2.0 sent without a placement ID leaves the temp's
                state — and so the burden rate — unknown. Said on the row, so it
                is seen before anyone opens the backing records. */}
            {!!invoice?.linesMissingPlacement && (
              <span className="tbr-note is-warn">
                {invoice?.linesMissingPlacement} line(s) missing placement ID
              </span>
            )}
          </div>
          <div className="tbr-secondary tbr-num">{asText(invoice?.invoiceDate)}</div>
          <div className="tbr-right tbr-num tbr-strong">
            {moneyFormatter(invoice?.invoiceAmountTotal || 0)}
          </div>
          <div className="tbr-right tbr-num tbr-secondary">
            {moneyFormatter(invoice?.weeklyActivityTotal || 0)}
          </div>
          <div className="tbr-right tbr-num tbr-secondary">
            {moneyFormatter(invoice?.profitSummaryTotal || 0)}
          </div>
          <div className={`tbr-diff${invoice?.matched ? '' : ' is-danger'}`}>
            {moneyFormatter(invoice?.difference || 0)}
          </div>
          {owedCell(invoice)}
          <div className="tbr-match">{calcPill(invoice)}</div>
          <div className="tbr-match">{matchPill(invoice)}</div>
          {statusCell(invoice)}
        </div>

        {open && backingRecords(invoice)}
      </Fragment>
    )
  }

  const groupCard = (group: IInvoiceGroup) => {
    const allSelected =
      !!group.visible.length && group.visible.every((invoice) => selected[invoice?.invoiceKey])
    // kan-125: the company-level Clear Selected. Same rule as the toolbar's —
    // visible, ticked, open — but scoped to this card, so a long payment can be
    // worked company by company without scrolling back to the top. Passed as
    // row-level so only this company's ticks drop on success; selections on
    // the other cards survive.
    const groupOpenKeys = group.visible
      .filter((invoice) => selected[invoice?.invoiceKey] && invoice?.status === 'OPEN')
      .map((invoice) => invoice?.invoiceKey)

    return (
      <section className="tbr-group" key={group.client}>
        <header className="tbr-group-head">
          <div className="tbr-group-title">
            <span className="tbr-group-name">{group.client}</span>
            <span className="tbr-group-count">{group.rows.length} invoice(s)</span>
          </div>
          <div className="tbr-group-side">
            {/* Both aggregates describe the group's WHOLE invoice set, not the
                filtered subset — otherwise a group reads "0 of 0 settled" the
                moment the open-only filter hides its settled rows. */}
            <div className="tbr-group-meta">
              <span>{moneyFormatter(group.amountTotal)}</span>
              <span>{group.settledCount} of {group.rows.length} settled</span>
            </div>
            <div className="tbr-group-actions">
              <span className="tbr-selection">
                {groupOpenKeys.length ? `${groupOpenKeys.length} selected` : 'None selected'}
              </span>
              <button
                type="button"
                className="tbr-btn is-sm is-action"
                title={`Clear the selected open invoices for ${group.client}`}
                disabled={busy || !groupOpenKeys.length}
                onClick={() => runClear(groupOpenKeys, true)}
              >
                Clear Selected
              </button>
            </div>
          </div>
        </header>

        <div className="tbr-scroll">
          <div className="tbr-inv-head">
            <div className="tbr-tick">
              <input
                type="checkbox"
                title={`Select every visible invoice for ${group.client}`}
                checked={allSelected}
                disabled={!group.visible.length}
                onChange={({ target: { checked } }) => toggleGroup(group, checked)}
              />
            </div>
            <div />
            <div>Invoice #</div>
            <div>Employee</div>
            <div>Inv. Date</div>
            <div className="tbr-right">Invoice Amt</div>
            <div className="tbr-right">Weekly Activity</div>
            <div className="tbr-right">Profit Summary</div>
            <div className="tbr-right">Difference</div>
            <div className="tbr-right">Owed to RealREPP</div>
            <div className="tbr-center">Calc</div>
            <div className="tbr-center">Match</div>
            <div className="tbr-right">Status</div>
          </div>

          {group.visible.map((invoice) => invoiceRow(invoice))}

          {/* The company tally, under the columns it sums. Like the header, it
              covers the client's WHOLE invoice set — the label says how many — so
              it stays the figure to tie against the client's statement whatever
              the filters are hiding. */}
          <div className="tbr-inv-foot">
            <div />
            <div />
            <div className="tbr-strong">Company total</div>
            <div className="tbr-secondary">{group.rows.length} invoice(s)</div>
            <div />
            <div className="tbr-right tbr-num tbr-strong">{moneyFormatter(group.amountTotal)}</div>
            <div className="tbr-right tbr-num tbr-strong">{moneyFormatter(group.weeklyTotal)}</div>
            <div className="tbr-right tbr-num tbr-strong">{moneyFormatter(group.profitTotal)}</div>
            <div
              className={`tbr-diff${
                Math.abs(group.weeklyTotal - group.profitTotal) < 0.005 ? '' : ' is-danger'
              }`}
            >
              {moneyFormatter(group.weeklyTotal - group.profitTotal)}
            </div>
            {/* Owed covers only the FULLY calculated invoices — a partial or
                state-unknown total would understate it — and says how many. */}
            {group.owedCount
              ? (
                <div
                  className="tbr-owed-cell"
                  title="Sum of Owed to RealREPP over the fully calculated invoices only"
                >
                  <span className="tbr-strong">{moneyFormatter(group.owedTotal)}</span>
                  <span className="tbr-note">
                    {group.owedCount} of {group.rows.length} calculated
                  </span>
                </div>
              )
              : <div />}
            <div />
            <div />
            <div />
          </div>
        </div>

        {!group.visible.length && (
          <div className="tbr-empty">No invoices match the current filters.</div>
        )}
      </section>
    )
  }

  return <div className="tbr-detail">
    <Link className="tbr-back" to={TEMP_BILLING_RECONCILIATION_ROUTE}>
      <BackIcon />
      <span>Back to Temp Billing</span>
    </Link>

    <header className="tbr-header">
      <div className="tbr-eyebrow">Invoice Reconciliation</div>
      <div className="tbr-heading-row">
        <h1 className="tbr-title">{payment?.paymentReferenceId || 'Unstamped Payment'}</h1>

        {stamped && <span className="tbr-pill is-active">Stamped</span>}

        <button
          type="button"
          className="tbr-icon-btn"
          title="Copy payment ref"
          disabled={!payment?.paymentReferenceId}
          onClick={copyReference}
        >
          <CopyIcon />
        </button>

        <div className="tbr-header-actions">
          {/* The settling-time snapshot of everything this payment paid, as a
              copy-to-Excel sheet. Nothing is settled yet => nothing to list. */}
          <PaidInvoicesModal
            paymentMetaId={paymentMetaId}
            className="tbr-icon-btn"
            disabled={!payment?.hasReconciliationRows}
          />
        </div>
      </div>
    </header>

    {loading && <Loader loaderClass="sm-height " />}

    {!loading && error && (
      <p className="tbr-error">{error?.message || 'Unable to load this payment'}</p>
    )}

    {!loading && !error && !!payment && (
      <>
        {/* ---- step bar: the three gated steps, in order ---- */}
        <div className="tbr-steps">
          <div className={`tbr-step${stamped ? '' : ' is-current'}`}>
            <span className={`tbr-step-marker ${stamped ? 'is-done' : 'is-current'}`}>
              {stamped ? <CheckIcon /> : '1'}
            </span>
            <div className="tbr-step-body">
              <span className={`tbr-step-title${stamped ? '' : ' is-current'}`}>
                Stamp Payment
              </span>
              <span className="tbr-step-sub">
                {stamped
                  ? `${payment?.paymentReferenceId || '--'} · ${dateText(payment?.paymentReceivedDate)}`
                  : 'Received date + payment key'}
              </span>
            </div>
            <button
              type="button"
              className="tbr-btn is-sm is-outline-brand"
              onClick={() => setStampOpen(true)}
            >
              {stamped ? 'Edit Stamp' : 'Stamp'}
            </button>
          </div>

          <div className={`tbr-step${stamped && !reconciled ? ' is-current' : ''}`}>
            <span
              className={`tbr-step-marker ${
                reconciled ? 'is-done' : stamped ? 'is-current' : ''
              }`}
            >
              {reconciled ? <CheckIcon /> : '2'}
            </span>
            <div className="tbr-step-body">
              <span
                className={`tbr-step-title${
                  stamped ? (reconciled ? '' : ' is-current') : ' is-locked'
                }`}
              >
                Reconciliation
              </span>
              <span className={`tbr-step-sub${stamped ? '' : ' is-locked'}`}>
                {!stamped
                  ? 'Stamp the payment first'
                  : `${settledCount} of ${invoiceCount} settled${
                      orphanCount ? ` · ${orphanCount} orphan(s) blocking` : ''
                    }`}
              </span>
            </div>
          </div>

          {/* Step 3 IS the activity-report assignment — the last step of the whole
              feature — so the control lives here rather than in a summary card.
              It stays reachable once assigned, because REMOVING the tag is what
              re-enables reopening. */}
          <div className={`tbr-step${activityUnlocked && !activityMonth ? ' is-current' : ''}`}>
            <span
              className={`tbr-step-marker ${
                activityMonth ? 'is-done' : activityUnlocked ? 'is-current' : ''
              }`}
            >
              {activityMonth ? <CheckIcon /> : '3'}
            </span>
            <div className="tbr-step-body">
              <span
                className={`tbr-step-title${
                  activityMonth ? '' : activityUnlocked ? ' is-current' : ' is-locked'
                }`}
              >
                Activity Report
              </span>
              {activityUnlocked
                ? <ActivityReportInput payment={payment} refetch={refetch} />
                : (
                  <span className="tbr-step-sub is-locked">
                    Locked until every invoice is settled
                  </span>
                )}
            </div>
          </div>
        </div>

        {/* ---- summary cards ---- */}
        <div className="tbr-summary">
          <div className="tbr-summary-card">
            <div className="tbr-summary-label">File</div>
            <Link
              className="tbr-file-link"
              title={payment?.fileName || ''}
              to={TEMP_BILLING_IMPORTS_ROUTE}
            >
              {asText(payment?.fileName)}
            </Link>
            <div className="tbr-summary-sub">
              {payment?.monthYr ? moment(payment?.monthYr).format('MMM-YYYY') : '--'}
              {payment?.uploadedByName ? ` · ${payment?.uploadedByName}` : ''}
            </div>
          </div>

          <div className="tbr-summary-card">
            <div className="tbr-summary-label">Payment Received</div>
            <div className="tbr-summary-value">{dateText(payment?.paymentReceivedDate)}</div>
            <div className="tbr-summary-sub">
              {moneyFormatter(payment?.profitSummaryTotal || 0)} total
            </div>
            {!stamped && (
              <div className="tbr-summary-sub is-danger">
                Stamp the payment before settling anything.
              </div>
            )}
          </div>

          <div className="tbr-summary-card">
            <div className="tbr-summary-label">Invoices Settled</div>
            <div className="tbr-summary-value">{settledCount} of {invoiceCount}</div>
            <div className="tbr-meter">
              <div className="tbr-meter-fill" style={{ width: `${settledPercent}%` }} />
            </div>
            <div className="tbr-summary-sub">{remaining} invoices remaining</div>
            {/* The breakdown matters to an accountant: Accepted and Suspense
                settle the payment, but they are not the same as Cleared. */}
            <div className="tbr-summary-sub">
              {clearedCount} cleared · {acceptedCount} accepted · {suspenseCount} in suspense
            </div>
            <div className="tbr-summary-sub">
              {moneyFormatter(payment?.settledProfitTotal || 0)} of{' '}
              {moneyFormatter(payment?.profitSummaryTotal || 0)} settled
            </div>
          </div>

          <div className="tbr-summary-card">
            <div className="tbr-summary-label">Unresolved Orphans</div>
            <div className={`tbr-summary-value${orphanCount ? ' is-danger' : ''}`}>
              {orphanCount} orphan(s)
            </div>
            <div className="tbr-summary-sub">
              Report unlocks when every invoice is cleared, accepted or in suspense, and no
              unresolved orphans remain.
            </div>
            {!!payment?.unkeyedRowCount && (
              <div className="tbr-summary-sub">
                {payment?.unkeyedRowCount} imported row(s) carry no invoice number
              </div>
            )}
          </div>
        </div>

        {/* ---- filter / bulk toolbar ---- */}
        <div className="tbr-toolbar">
          <div className="tbr-filters">
            <label className="tbr-check">
              <input
                type="checkbox"
                checked={onlyOpen}
                // Suspense items are settled, so the two filters cannot both
                // apply — Suspense only wins while it is on.
                disabled={onlySuspense}
                title={onlySuspense ? 'Ignored while "Suspense only" is on' : ''}
                onChange={({ target: { checked } }) => setOnlyOpen(checked)}
              />
              <span>Show only open invoices</span>
            </label>

            <label className="tbr-check">
              <input
                type="checkbox"
                checked={onlyMismatch}
                onChange={({ target: { checked } }) => setOnlyMismatch(checked)}
              />
              <span>Mismatches only</span>
            </label>

            <label className="tbr-check">
              <input
                type="checkbox"
                checked={onlySuspense}
                onChange={({ target: { checked } }) => setOnlySuspense(checked)}
              />
              <span>Suspense only</span>
            </label>

            {/* Owed to RealREPP vs what People 2.0 paid — a separate question
                from "Mismatches only", which compares the two People 2.0 reports
                with each other. Combines with the other filters. */}
            <label
              className="tbr-check"
              title="Invoices where our recalculation differs from the paid recruiter profit by more than $0.05, or cannot be made until a placement ID is set"
            >
              <input
                type="checkbox"
                checked={onlyCalcMismatch}
                onChange={({ target: { checked } }) => setOnlyCalcMismatch(checked)}
              />
              <span>Calc mismatches only</span>
            </label>
          </div>

          <div className="tbr-bulk">
            <span className="tbr-selection">
              {selectedTotal ? `${selectedTotal} selected` : 'No invoices selected'}
            </span>
            <button
              type="button"
              className="tbr-btn is-outline-neutral"
              title={activityMonth
                ? 'Remove the activity report before reopening anything'
                : 'Reopen the selected cleared or flagged invoices'}
              disabled={busy || !selectedSettledKeys.length || !!activityMonth}
              onClick={() => runUnclear(selectedSettledKeys)}
            >
              Reopen Selected
            </button>
            <button
              type="button"
              className="tbr-btn is-action"
              disabled={busy || !selectedOpenKeys.length}
              onClick={() => runClear(selectedOpenKeys)}
            >
              Clear Selected
            </button>
          </div>
        </div>

        {!!activityMonth && (
          <div className="tbr-notice">
            An activity report ({moment(activityMonth).format('MMM-YY')}) is assigned, so invoices
            on this payment can no longer be reopened. Remove it in step 3 first.
          </div>
        )}

        {!invoices?.length && (
          <div className="tbr-group">
            <div className="tbr-empty">No invoices were imported under this payment.</div>
          </div>
        )}

        {groups.map((group) => groupCard(group))}
      </>
    )}

    <StampPaymentModal
      isOpen={stampOpen}
      toggle={() => setStampOpen(false)}
      payment={payment}
      refetch={refetch}
    />

    <FlagInvoiceModal
      isOpen={!!flagTarget}
      toggle={() => setFlagTarget(null)}
      paymentMetaId={paymentMetaId}
      invoice={flagTarget}
      refetch={refetch}
    />
  </div>
}

export default ReconciliationDetail
